<template>
  <div class="z-index-2">
    <div class="p-3 pb-0">
      <h6>{{ title }}</h6>
    </div>
    <div v-if="alert" class="pt-0">
      <alert
        class="mt-0 mb-5 ms-3 me-4"
        title="Warning"
        body="The recovery score has been below the 14-day average for the past four days, suggesting a potential risk of injuries due to over-exertion. Be vigilant and mindful of the possibility of such injuries."
        :show-icon="true"
        color="warning"
        bg-color="info"
      />
    </div>
    <div class="p-3 pt-0">
      <div class="chart">
        <canvas :id="id" class="chart-canvas" :height="height"></canvas>
      </div>
    </div>
  </div>
</template>

<script>
import ChartUtils from "@/helpers/chartUtils";
import Chart from "chart.js/auto";
import annotationPlugin from "chartjs-plugin-annotation";
import styles from "./UserRecoveryChart.vue?vue&type=style&index=0&lang=scss&module=1";
import Alert from "@/shared/Cards/Alert.vue";

Chart.register(annotationPlugin);

export default {
  name: "UserRecoveryChart",
  components: {
    Alert,
  },
  props: {
    id: {
      type: String,
      default: "user-recovery-chart",
    },
    title: {
      type: String,
      default: "",
    },
    height: {
      type: String,
      default: "300",
    },
    chart: {
      type: Object,
      required: true,
      labels: Array,
      datasets: {
        type: Array,
        label: String,
        data: Array,
      },
    },
  },
  data() {
    return {
      alert: null,
    };
  },
  watch: {
    chart() {
      this.addChart();
    },
  },
  mounted() {
    Chart.defaults.borderColor = styles.darkGray;

    this.addChart();
  },
  methods: {
    addChart() {
      const ctx = document.getElementById(this.id).getContext("2d");

      let gradientStroke1 = ctx.createLinearGradient(0, 300, 0, 50);
      gradientStroke1.addColorStop(1, "rgba(59,121,130,0.15)");
      gradientStroke1.addColorStop(0.5, "transparent");
      gradientStroke1.addColorStop(0, "transparent");

      let chartStatus = Chart.getChart(this.id);
      if (chartStatus != undefined) {
        chartStatus.destroy();
      }

      let y1Max = Math.ceil(Math.max(...this.chart.datasets[0].data) / 10) * 10;
      let yMax = Math.ceil(Math.max(...this.chart.datasets[1].data) / 10) * 10;

      new Chart(ctx, {
        data: {
          labels: this.chart.labels,
          datasets: [
            {
              type: "bar",
              label: this.chart.datasets[0].label,
              weight: 5,
              tension: 0.4,
              borderWidth: 0,
              pointBackgroundColor: styles.colorRecovery,
              borderColor: styles.colorRecovery,
              backgroundColor: styles.colorRecovery,
              borderRadius: 4,
              borderSkipped: false,
              data: this.chart.datasets[0].data,
              maxBarThickness: 30,
              order: 2,
              yAxisID: "y1",
            },
            {
              type: "line",
              label: this.chart.datasets[1].label,
              tension: 0.4,
              borderWidth: 0,
              pointRadius: 3,
              pointBackgroundColor: styles.colorHrv,
              borderColor: styles.colorHrv,
              // eslint-disable-next-line no-dupe-keys
              borderWidth: 3,
              backgroundColor: gradientStroke1,
              data: this.chart.datasets[1].data,
              fill: true,
              order: 1,
              yAxisID: "y",
            },
          ],
        },
        options: {
          responsive: true,
          maintainAspectRatio: false,
          plugins: {
            annotation: {
              annotations: {
                line1: {
                  type: "line",
                  borderColor: styles.colorRecovery,
                  borderDash: [6, 6],
                  borderDashOffset: 0,
                  borderWidth: 1,
                  label: {
                    display: true,
                    content: (ctx) =>
                      `Avg Recovery: ${Math.round(ChartUtils.average(ctx, 0))}%`,
                    position: "start",
                  },
                  scaleID: "y1",
                  value: (ctx) => ChartUtils.average(ctx, 0),
                },
                line2: {
                  type: "line",
                  borderColor: styles.colorHrv,
                  borderDash: [6, 6],
                  borderDashOffset: 0,
                  borderWidth: 1,
                  label: {
                    display: true,
                    content: (ctx) =>
                      `Avg HRV: ${Math.round(ChartUtils.average(ctx, 1))} ms`,
                    position: "end",
                  },
                  scaleID: "y",
                  value: (ctx) => ChartUtils.average(ctx, 1),
                },
              },
            },
            legend: {
              position: "bottom",
              labels: {
                color: styles.colorAxes,
                boxWidth: 12,
                boxHeight: 12,
                padding: 40,
              },
            },
          },
          interaction: {
            intersect: false,
            mode: "index",
          },
          scales: {
            y1: {
              display: true,
              position: "left",
              min: 0,
              max: y1Max,
              ticks: {
                stepSize: 10,
                color: styles.gray,
              },
              title: {
                display: true,
                text: this.chart.datasets[0].label,
                color: styles.colorAxes,
                font: {
                  family: "NotoSans-Regular",
                },
              },
              // grid line settings
              grid: {
                drawOnChartArea: false, // only want the grid lines for one axis to show up
              },
            },
            x: {
              grid: {
                drawBorder: false,
                display: true,
                drawOnChartArea: true,
                drawTicks: true,
                borderDash: [5, 5],
              },
              ticks: {
                display: true,
                color: styles.gray,
                padding: 10,
                font: {
                  size: 12,
                  style: "normal",
                  lineHeight: 2,
                },
              },
            },
            y: {
              display: true,
              position: "right",
              min: 0,
              max: yMax,
              ticks: {
                stepSize: 2,
                color: styles.gray,
              },
              title: {
                display: true,
                text: this.chart.datasets[1].label,
                color: styles.colorAxes,
                font: {
                  family: "NotoSans-Regular",
                },
              },
              // grid line settings
              grid: {
                drawOnChartArea: true,
              },
            },
          },
        },
      });
    },
  },
};
</script>
<style lang="scss" scoped>
:export {
  darkSecondary: $g-secondary_600;
  secondary: $g-secondary_500;
  gray: $color-muted-text;
  darkGray: $color-gridlines;
  colorRecovery: $color-recovery;
  colorHrv: $g-secondary_200;
  colorAxes: $color-chart-axes-text;
  colorLimitline: $color-chart-limitline;
  red: $g-red_500;
  green: $g-cableGreen_500;
  blue: $g-blue_500;
}
</style>
